import React, { Component } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Spring, animated } from 'react-spring';

import StorageHandler from 'config/StorageHandler';
import { storageKeys } from 'config/settings.json';
import i18n from 'i18n-react';

const ROW_HEIGHT_PX = 144; // Tighter spacing for right column animation

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function buildMatchData(props) {
  const pairs = [
    { left: props.left_text_one, right: props.right_text_one },
    { left: props.left_text_two, right: props.right_text_two },
    { left: props.left_text_three, right: props.right_text_three },
    { left: props.left_text_four, right: props.right_text_four },
  ].filter((pair) => pair.left && pair.right);

  const leftSide = pairs.map((p) => p.left);
  const rightSide = pairs.map((p) => p.right);
  const correctAnswers = Object.fromEntries(pairs.map((p) => [p.left, p.right]));

  return { leftSide, rightSide, correctAnswers };
}

export default class MatchTheSentence extends Component {
  constructor(props) {
    super(props);

    const { leftSide, rightSide, correctAnswers } = buildMatchData(props);

    this.correctAnswers = correctAnswers;

    this.state = {
      selectedLeft: null,
      lockedMatches: [],
      incorrectMatch: false,
      incorrectRight: null,
      rightOrder: shuffleArray(rightSide),
      prevRightOrder: null,
      leftSide,
      headerImage: '',
    };

    this.handleLeftSelect = this.handleLeftSelect.bind(this);
    this.handleRightSelect = this.handleRightSelect.bind(this);
    this.isAlreadyMatched = this.isAlreadyMatched.bind(this);
  }

  componentDidMount() {
    this.setHeader();
    const firstBtn = document.querySelector('.leftSide .match__button');
    if (firstBtn) {
      const style = getComputedStyle(firstBtn);
      const marginTop = parseFloat(style.marginTop);
      this.rowHeight = firstBtn.offsetHeight + marginTop;
    }
  }

  componentDidUpdate(prevProps) {
    const { quizMode, quizTimerExpired, error } = this.props;
    if (quizMode && quizTimerExpired && !prevProps.quizTimerExpired) {
      setTimeout(() => error(true), 600);
    }
  }

  componentWillUnmount() {
    if (this._reorderTimeoutId) clearTimeout(this._reorderTimeoutId);
  }

  async setHeader() {
    const appInfo = await StorageHandler.load(storageKeys.appInfo);
    const { questionHeader } = appInfo;
    this.setState({ headerImage: questionHeader });
  }

  handleLeftSelect(left) {
    this.setState({ selectedLeft: left });
  }

  handleRightSelect(right) {
    const { selectedLeft, lockedMatches, rightOrder } = this.state;
    const { contentComplete, correctAnswerText, error } = this.props;

    if (selectedLeft && this.correctAnswers[selectedLeft] === right) {
      const newLocked = [...lockedMatches, { left: selectedLeft, right }];

      const newOrder = [...rightOrder];
      const leftIndex = this.state.leftSide.indexOf(selectedLeft);
      const rightIndex = newOrder.indexOf(right);

      [newOrder[leftIndex], newOrder[rightIndex]] = [newOrder[rightIndex], newOrder[leftIndex]];

      this.setState(
        {
          lockedMatches: newLocked,
          selectedLeft: null,
          incorrectMatch: false,
          incorrectRight: null,
          prevRightOrder: rightOrder,
          rightOrder: newOrder,
        },
        () => {
          if (newLocked.length === this.state.leftSide.length) {
            contentComplete();
          }
          // Clear prev order after animation so next reorder animates from correct position
          this._reorderTimeoutId = setTimeout(() => {
            this.setState({ prevRightOrder: null });
          }, 450);
        },
      );
    } else {
      this.setState({
        incorrectMatch: true,
        incorrectRight: right,
      });

      setTimeout(() => {
        this.setState({
          incorrectMatch: false,
          incorrectRight: null,
          selectedLeft: null,
        });

        error(true);
      }, 1000);
    }
  }

  isAlreadyMatched(sentence) {
    return this.state.lockedMatches.some((match) => match.left === sentence || match.right === sentence);
  }

  render() {
    const { question, disableHeader, image, quizMode } = this.props;
    const { selectedLeft, incorrectMatch, incorrectRight, rightOrder, prevRightOrder, headerImage } =
      this.state;
    const questionImage = image || headerImage;

    return (
      <div className={clsx('wrapper__matchSentences', 'container__question', { 'quiz-mode-card': quizMode })}>
        {!disableHeader && (
          <div className="container__question--image" style={{ backgroundImage: `url(${questionImage})` }} />
        )}
        <div className="container__question--content">
          <h4 className="container__title">{question}</h4>
          <p className="match__description">{i18n.translate('questions.matchTheSentence')}</p>
          <div className="match__container">
            <div className="leftSide">
              {this.state.leftSide.map((left) => (
                <button
                  key={left}
                  onClick={() => this.handleLeftSelect(left)}
                  disabled={this.isAlreadyMatched(left)}
                  className={clsx('match__button', {
                    selected: selectedLeft === left,
                    matched: this.isAlreadyMatched(left),
                    incorrect: incorrectMatch && selectedLeft === left,
                  })}
                >
                  {left}
                </button>
              ))}
            </div>
            <div className="rightSide rightSide--animated">
              {rightOrder.map((right, index) => {
                const prevIndex = prevRightOrder ? prevRightOrder.indexOf(right) : index;
                const fromY = prevIndex * (this.rowHeight || ROW_HEIGHT_PX);
                const toY = index * (this.rowHeight || ROW_HEIGHT_PX);
                const immediate = !prevRightOrder;

                return (
                  <Spring
                    key={right}
                    native
                    from={{ transform: `translateY(${fromY}px)` }}
                    to={{ transform: `translateY(${toY}px)` }}
                    immediate={immediate}
                    config={{ tension: 280, friction: 24 }}
                  >
                    {(styles) => (
                      <animated.div className="rightSide__item" style={styles}>
                        <button
                          onClick={() => {
                            if (selectedLeft && !this.isAlreadyMatched(right)) {
                              this.handleRightSelect(right);
                            }
                          }}
                          disabled={this.isAlreadyMatched(right)}
                          className={clsx('match__button', {
                            inactive: !selectedLeft && !this.isAlreadyMatched(right),
                            matched: this.isAlreadyMatched(right),
                            incorrect: incorrectMatch && incorrectRight === right,
                          })}
                        >
                          {right}
                        </button>
                      </animated.div>
                    )}
                  </Spring>
                );
              })}
            </div>
          </div>
        </div>
        {this.props.correct(false)}
        {this.props.error(false)}
      </div>
    );
  }
}


MatchTheSentence.propTypes = {
  left_text_one: PropTypes.string,
  left_text_two: PropTypes.string,
  left_text_three: PropTypes.string,
  left_text_four: PropTypes.string,
  right_text_one: PropTypes.string,
  right_text_two: PropTypes.string,
  right_text_three: PropTypes.string,
  right_text_four: PropTypes.string,
  contentComplete: PropTypes.func,
  correct: PropTypes.func,
  correctAnswerText: PropTypes.string,
  incorrectAnswerText: PropTypes.string,
  disableHeader: PropTypes.bool,
  error: PropTypes.func,
  image: PropTypes.string,
  question: PropTypes.string,
  title: PropTypes.string,
  quizMode: PropTypes.bool,
  quizTimerExpired: PropTypes.bool,
};

MatchTheSentence.defaultProps = {
  contentComplete: () => undefined,
  correct: () => undefined,
  correctAnswerText: 'Correct!',
  incorrectAnswerText: 'Try again',
  disableHeader: false,
  error: () => undefined,
  image: '',
  question: '',
  title: '',
  quizMode: false,
  quizTimerExpired: false,
};
